import { iconEnumToString } from "@storytell/bsky-icons";
import { AssociateLabelRequestConfig, apiFetch, CreateLabelRequestConfig, useStorytellClient } from "@storytell/client";
import { Icons } from "@storytell/cp-client";
import { type AssetId, type LabelAssociation, type LabelId, LabelSchema } from "@storytell/types";
import dayjs from "dayjs";
import type { Wire } from "~/wire/definition";

/**
 * Creates and associates labels with an asset:
 * 1. "Added by [username]" - avatar icon, blue background (only if user exists)
 * 2. "Added on [MMM DD YY HH:MM AM/PM]" - clock icon, gold background (always created)
 */
export async function createAutoLabelsForAsset(assetId: AssetId, authToken: string, wire: Wire): Promise<void> {
  const client = useStorytellClient();
  const user = wire.auth.user();

  try {
    const now = dayjs().startOf("minute").toDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[now.getMonth()];
    const day = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear()).slice(-2);

    const hours24 = now.getHours();
    const hours12 = hours24 === 0 ? 12 : hours24 > 12 ? hours24 - 12 : hours24;
    const ampm = hours24 >= 12 ? "PM" : "AM";
    const minutes = String(now.getMinutes()).padStart(2, "0");

    const addedOnLabelName = `Added on ${month} ${day} ${year} ${hours12}:${minutes} ${ampm}`;

    let addedByLabelName: string | undefined;
    if (user) {
      const username = user.displayName || user.primaryEmail?.split("@")[0] || "User";
      addedByLabelName = `Added by ${username}`;
    }

    const labelsResponse = await apiFetch("/labels", {
      method: "GET",
      authToken,
      scope: "project-scoped",
    });

    let addedByLabelId: string | undefined;
    let addedOnLabelId: string | undefined;

    if (labelsResponse.ok) {
      const body: unknown = await labelsResponse.json();
      const labels = (Array.isArray(body) ? body : []).flatMap((raw: unknown) => {
        const parsed = LabelSchema.safeParse(raw);
        return parsed.success ? [parsed.data] : [];
      });
      if (addedByLabelName) {
        const existingAddedByLabel = labels.find((label) => label.name === addedByLabelName);
        if (existingAddedByLabel) {
          addedByLabelId = existingAddedByLabel.id;
          wire.labels.set(existingAddedByLabel.id, existingAddedByLabel);
        }
      }

      const existingAddedOnLabel = labels.find((label) => label.name === addedOnLabelName);
      if (existingAddedOnLabel) {
        addedOnLabelId = existingAddedOnLabel.id;
        wire.labels.set(existingAddedOnLabel.id, existingAddedOnLabel);
      }
    }

    if (addedByLabelName && !addedByLabelId && user) {
      const username = user.displayName || user.primaryEmail?.split("@")[0] || "User";
      const createRequest = CreateLabelRequestConfig.withBearerToken(authToken);
      const createResult = await client.fetch(createRequest, undefined, {
        name: addedByLabelName,
        description: `Label indicating that this asset was added by ${username}`,
        icon: iconEnumToString(Icons.Icon.AVATAR),
        color: "#51a2ff",
      });

      if (createResult.isOk()) {
        addedByLabelId = createResult.value.id;
        const createdLabel = { ...createResult.value };
        wire.labels.set(createResult.value.id, createdLabel);
      } else {
        console.warn("Failed to create 'Added by' label:", createResult.error);
      }
    }

    if (!addedOnLabelId) {
      const createRequest = CreateLabelRequestConfig.withBearerToken(authToken);
      const createResult = await client.fetch(createRequest, undefined, {
        name: addedOnLabelName,
        description: "Label indicating when this asset was added",
        icon: iconEnumToString(Icons.Icon.CLOCK),
        color: "#ffd700",
      });

      if (createResult.isOk()) {
        addedOnLabelId = createResult.value.id;
        const createdLabel = { ...createResult.value };
        wire.labels.set(createResult.value.id, createdLabel);
      } else {
        console.warn("Failed to create 'Added on' label:", createResult.error);
      }
    }

    const labelsToAssociate: Array<{ labelId: string; resourceId: AssetId; resourceType: "asset" }> = [];

    if (addedByLabelId) {
      labelsToAssociate.push({
        labelId: addedByLabelId,
        resourceId: assetId,
        resourceType: "asset",
      });
    }

    if (addedOnLabelId) {
      labelsToAssociate.push({
        labelId: addedOnLabelId,
        resourceId: assetId,
        resourceType: "asset",
      });
    }

    if (labelsToAssociate.length > 0) {
      const associateRequest = AssociateLabelRequestConfig.withBearerToken(authToken);
      const associateResult = await client.fetch(associateRequest, undefined, labelsToAssociate);

      if (associateResult.isOk()) {
        if (addedByLabelId) {
          wire.rels.addLabelToAsset(assetId, addedByLabelId as LabelId);
        }
        if (addedOnLabelId) {
          wire.rels.addLabelToAsset(assetId, addedOnLabelId as LabelId);
        }
        associateResult.value.forEach((assoc) => {
          wire.labels.onLabelAssociated(assoc as LabelAssociation);
        });
      } else {
        console.warn("Failed to associate labels with asset:", associateResult.error);
      }
    }
  } catch (error) {
    // Non-critical error - log but don't fail the operation
    console.warn("Failed to create auto labels for asset:", error);
  }
}
